import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import type { ApiError } from '@/api/http';
import { queryKeyRoots } from '@/api/queryKeys';
import { createStaff, type CreatedStaff } from '@/api/users';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';

import { GROUP_OPTIONS } from './groups';

/** The three groups, and nothing else — the server refuses a fourth name before it writes. */
const colleagueSchema = z.object({
  email: z.string().trim().min(1, 'emailRequired').email('emailInvalid'),
  first_name: z.string().trim(),
  last_name: z.string().trim(),
  group: z.string().min(1, 'groupRequired'),
});

export type ColleagueForm = z.infer<typeof colleagueSchema>;

interface Props {
  onClose: () => void;
  /** Called with the created account and its **one-time link**, which the caller then shows once. */
  onCreated: (created: CreatedStaff) => void;
}

/**
 * §6.10's **Add a colleague** — the write that replaced an SSH session (backend TASK-099).
 *
 * ⚠️ **There is no password field, and its absence is the feature.** The account is created with an
 * unusable password and the response carries a one-time link the Admin hands over. An administrator
 * who could set — or read — a colleague's password could sign in as them and read candidates' feedback
 * as them, leaving their name on it; so the capability is absent rather than discouraged, and the
 * dialog's own description says how the person gets in instead.
 *
 * ⚠️ **Two guards hold that, not one, and the second was missing for a release** (BUG-027). This
 * dialog's missing field rests on `set_unusable_password` in the **create** route, which was never the
 * hole: `POST /users/{id}/invitation/` issued a link for *any* account in the tenant — an **active**
 * colleague who had already chosen their password included — and redeeming it set a new one, so an
 * Admin could hold that person's session. `issue_invitation` refuses that now, on every route that
 * calls it, and `UsersSection` renders the refusal. The claim above stands; what was false was that
 * one guard made it true.
 *
 * ⚠️ **And it stands only once the colleague has *claimed* the account.** The link this dialog's
 * response hands back sets a password, and redemption asks nothing about who redeems — so between
 * creating somebody and their first sign-in, the Admin holding the link can become them. That is the
 * cost of a copied link where nothing can send mail, not a defect in the guard, and an emailed
 * invitation is what would close it.
 *
 * ⚠️ **`group` is required with no default**, which is the same decision §6.9's category and difficulty
 * took: an account in **no** group can be assigned an interview and then cannot open it — a real state
 * of this product, which the directory's own *"No group"* cell exists to make visible — and defaulting
 * it would classify a colleague nobody classified.
 *
 * ⚠️ **The server's 422 is rendered as its own sentence, not translated into ours.** *"An account with
 * this email already exists"* carries the reason (sign-in is global, so one address is one account) and
 * the next step (change their group instead), and a client-side copy of that text would drift from the
 * rule the moment the rule changes. Only the shape of the failure is ours.
 */
export function AddColleagueDialog({ onClose, onCreated }: Props) {
  const { t } = useTranslation('settings');
  const queryClient = useQueryClient();

  const form = useForm<ColleagueForm>({
    resolver: zodResolver(colleagueSchema),
    defaultValues: { email: '', first_name: '', last_name: '', group: '' },
  });

  const create = useMutation<CreatedStaff, ApiError, ColleagueForm>({
    mutationFn: (values) => createStaff(values),
    onSuccess: (created) => {
      // The directory gains a row, and it carries `invitation_pending`.
      queryClient.invalidateQueries({ queryKey: queryKeyRoots.staff });
      onCreated(created);
    },
  });

  const messageFor = (key: string | undefined) => (key ? t(`add.${key}`) : undefined);

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t('add.title')}
      description={t('add.description')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common:cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            onClick={form.handleSubmit((values) => create.mutate(values))}
            disabled={create.isPending}
          >
            {t('add.submit')}
          </Button>
        </>
      }
    >
      <form
        className="space-y-4"
        onSubmit={form.handleSubmit((values) => create.mutate(values))}
        noValidate
      >
        <Field
          label={t('add.email')}
          required
          description={t('add.emailHint')}
          error={messageFor(form.formState.errors.email?.message)}
        >
          {(field) => (
            <Input type="email" autoComplete="off" {...field} {...form.register('email')} />
          )}
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('add.firstName')}>
            {(field) => <Input {...field} {...form.register('first_name')} />}
          </Field>
          <Field label={t('add.lastName')} description={t('add.nameHint')}>
            {(field) => <Input {...field} {...form.register('last_name')} />}
          </Field>
        </div>

        <Field
          label={t('add.group')}
          required
          description={t('add.groupHint')}
          error={messageFor(form.formState.errors.group?.message)}
        >
          {(field) => (
            <Select
              options={GROUP_OPTIONS.map((group) => ({ value: group, label: group }))}
              value={form.watch('group')}
              onValueChange={(value) => form.setValue('group', value, { shouldValidate: true })}
              {...field}
            />
          )}
        </Field>

        {create.isError && (
          <p className="text-xs text-negative">{create.error.detail ?? t('add.failed')}</p>
        )}
      </form>
    </Dialog>
  );
}
